import React from "react";
import { useToast } from "./ToastServices";

// Reusable Copy Button Component
export const CopyButton = ({ text }) => {
  const toast = useToast();
  if (!text) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(text);
        if (toast) {
          toast.open({
            type: "success",
            message: "Copied",
            description: `Copied "${text}" to clipboard`
          });
        }
      }}
      className="text-gray-400 hover:text-[#0a58ca] ml-1.5 p-0.5 rounded hover:bg-gray-100 transition-colors inline-flex items-center justify-center shrink-0"
      title="Copy to clipboard"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
    </button>
  );
};

// Reusable Pagination Component
export const PremiumPagination = ({
  pageIndex,
  setPageIndex,
  totalPages,
  dataCount,
  setDataCount,
  isCardView = false
}) => {
  if (totalPages <= 0) return null;

  return (
    <div className={`flex justify-between items-center w-full px-5 py-4 border text-xs font-semibold text-gray-500 select-none bg-white ${isCardView ? "border-gray-200/80 rounded-2xl shadow-sm" : "border-t border-gray-100"}`}>
      {/* Left side: Per Page Select */}
      {setDataCount && (
        <div className="flex items-center gap-2">
          <span className="hidden lg:block">View</span>
          <select
            value={dataCount}
            onChange={(e) => {
              const val = Number(e.target.value);
              setDataCount(val);
              localStorage.setItem("perPageView", val);
              setPageIndex(1);
            }}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="hidden lg:block">: Per Page</span>
        </div>
      )}
      {!setDataCount && (
        <div >
          Showing {pageIndex} of {totalPages} {totalPages === 1 ? "page" : "pages"}
        </div>
      )}

      {/* Right side: Page navigation */}
      <div className="flex items-center gap-1.5">
        {setDataCount && (
          <div className="mr-2 text-gray-500">
            <span className="hidden lg:inline-block lg:me-2">
              Showing
            </span>
            <span className="text-gray-700 font-bold">{pageIndex}</span> of <span className="text-gray-700 font-bold">{totalPages}</span>
            <span className="hidden lg:inline-block lg:ms-2">{totalPages === 1 ? "Page" : "Pages"}</span>
          </div>
        )}

        {/* First Button */}
        <button
          onClick={() => setPageIndex(1)}
          disabled={pageIndex === 1}
          className="p-1 px-2 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          title="First Page"
        >
          &lt;&lt;
        </button>

        {/* Previous Button */}
        <button
          onClick={() => setPageIndex(pageIndex > 1 ? pageIndex - 1 : 1)}
          disabled={pageIndex === 1}
          className="p-1 px-2.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          title="Previous Page"
        >
          &lt;
        </button>

        {/* Active Page Indicator */}
        {/* <button className="p-1 hidden lg:block px-3 border border-emerald-600 text-emerald-600 rounded bg-emerald-50/10 font-bold">
          {pageIndex}
        </button> */}

        {/* Next Button */}
        <button
          onClick={() => setPageIndex(pageIndex < totalPages ? pageIndex + 1 : totalPages)}
          disabled={pageIndex === totalPages}
          className="p-1 px-2.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          title="Next Page"
        >
          &gt;
        </button>

        {/* Last Button */}
        <button
          onClick={() => setPageIndex(totalPages)}
          disabled={pageIndex === totalPages}
          className="p-1 px-2 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          title="Last Page"
        >
          &gt;&gt;
        </button>
      </div>
    </div>
  );
};

// Reusable PremiumTable Component
export const PremiumTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  viewMode = "table",
  headers = [],
  renderRow,
  renderCard,
  dataLoading = false,
  listofNumbers = [],
  selectMultipleFunction,
  fullList = [],
  showEdit = false,
  setShowEdit,
  multiEditLabel = "Multi Edit",
  height = "h-[calc(100vh-150px)]",
}) => {
  const totalPages = Math.ceil(total / dataCount) || 1;

  if (dataLoading) {
    return (
      <div className="w-full bg-white rounded-md py-12 flex justify-center items-center h-[50vh]">
        <div className="flex flex-row gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-600 animate-bounce" />
          <div className="w-3 h-3 rounded-full bg-emerald-600 animate-bounce [animation-delay:-.3s]" />
          <div className="w-3 h-3 rounded-full bg-emerald-600 animate-bounce [animation-delay:-.5s]" />
          <div className="w-3 h-3 rounded-full bg-emerald-600 animate-bounce [animation-delay:-.7s]" />
        </div>
      </div>
    );
  }

  if (viewMode === "card") {
    return (
      <div className={`w-full flex flex-col justify-between gap-6 mt-4 ${height}`}>
        <div className="flex-1 min-h-0 w-full overflow-y-auto">
          {data.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
              {data.map((row, idx) => renderCard(row, idx))}
            </div>
          ) : (
            <div className="w-full p-12 text-center text-gray-400 border border-gray-200/80 rounded-2xl bg-white shadow-sm">
              <div className="flex flex-col items-center justify-center">
                <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-xs font-semibold">No data found</span>
              </div>
            </div>
          )}
        </div>

        {data.length > 0 && (
          <div className="mt-auto">
            <PremiumPagination
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              totalPages={totalPages}
              dataCount={dataCount}
              setDataCount={setDataCount}
              isCardView={true}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full mt-4 flex flex-col justify-between ${height}`}>
      <div className="flex-1 min-h-0 w-full flex flex-col">
        {/* Multi-Edit Banner */}
        {listofNumbers.length > 0 && showEdit !== undefined && setShowEdit && (
          <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-100 flex justify-between items-center transition-all duration-300">
            <span className="text-xs font-semibold text-emerald-800">
              {listofNumbers.length} {listofNumbers.length === 1 ? "item" : "items"} selected
            </span>
            <button
              onClick={() => setShowEdit(!showEdit)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1 text-xs font-semibold rounded-md shadow-sm transition-colors duration-200"
            >
              {multiEditLabel}
            </button>
          </div>
        )}

        {/* Table Container */}
        <div className="w-full overflow-x-auto overflow-y-auto flex-1 min-h-0">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr className="text-xs font-bold text-gray-500 uppercase tracking-wider select-none bg-white sticky top-0 z-20">
                {selectMultipleFunction && (
                  <th className="p-4 text-center w-12 bg-white sticky top-0 z-20">
                    <input
                      type="checkbox"
                      onChange={selectMultipleFunction}
                      checked={fullList.length > 0 && listofNumbers.length === fullList.length}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4 cursor-pointer"
                    />
                  </th>
                )}
                {headers.map((header, idx) => (
                  <th
                    key={idx}
                    className={`p-4 font-bold bg-white sticky top-0 z-20 whitespace-nowrap ${header.align === "right"
                      ? "text-right"
                      : header.align === "center"
                        ? "text-center"
                        : "text-left"
                      }`}
                    style={{ width: header.width }}
                  >
                    {header.title}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Spacer between header and table content */}
            <tbody className="h-3 bg-[#F1F5F9] border-0 select-none pointer-events-none">
              <tr className="h-3 bg-[#F1F5F9] border-0">
                <td colSpan={headers.length + (selectMultipleFunction ? 1 : 0)} className="p-0 border-0 bg-[#F1F5F9] h-3"></td>
              </tr>
            </tbody>

            {data.length > 0 ? (
              data.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tbody className="bg-white rounded-xl drop-shadow-[0_4px_10px_rgba(0,0,0,0.03)] hover:drop-shadow-[0_10px_20px_rgba(0,0,0,0.07)] transition-all duration-300">
                    {renderRow(row, idx)}
                  </tbody>
                  <tbody className="h-3 bg-[#F1F5F9] border-0 select-none pointer-events-none">
                    <tr className="h-3 bg-[#F1F5F9] border-0">
                      <td colSpan={headers.length + (selectMultipleFunction ? 1 : 0)} className="p-0 border-0 bg-[#F1F5F9] h-3"></td>
                    </tr>
                  </tbody>
                </React.Fragment>
              ))
            ) : (
              <tbody className="bg-white rounded-xl shadow-sm">
                <tr>
                  <td colSpan={headers.length + (selectMultipleFunction ? 1 : 0)} className="p-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="text-xs font-semibold">No data found</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>

      {data.length > 0 && (
        <PremiumPagination
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          totalPages={totalPages}
          dataCount={dataCount}
          setDataCount={setDataCount}
          isCardView={false}
        />
      )}
    </div>
  );
};

export default PremiumTable;
